import React, { useMemo, useState } from "react";
import { Auth } from "aws-amplify";
import { Link } from "react-router-dom";
import { useGetMyMemberDashboardChambersQuery } from "../../../graphql/types";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import { selectSignedInMemberChamber } from "../../pages/Login/SignInHandler";
import OneChamberSystemLogo from "../../../images/osc_logo.png";

// Monday M1/M3/M5 (Member Dashboard (1) design, CMP Accelerator L39-40,
// L1716-1790): "Switch Chamber" under the sidebar title when one login is
// linked to more than one chamber; a selection window; and a chamber login
// page before another chamber opens.

const GOLD = "#E1A731";
const GOLD_DARK = "#B7791F";
const BLACK = "#0A0A0A";

export type SwitchChamberRow = { chamberId: string; name: string; logo?: string | null; isActive: boolean; isHome: boolean };

export const getOrganizationNoun = (chamberName?: string | null) =>
  /board of trade/i.test(chamberName || "") ? "Board of Trade" : "Chamber";

const fill = (template: string, values: Record<string, string>) =>
  Object.keys(values).reduce((text, key) => text.split(`{{${key}}}`).join(values[key]), template);

const logoBox = (logo: string | null | undefined, name: string, width: number, height: number) => (
  <div role="img" aria-label={name} style={{ width, height, backgroundImage: logo ? `url("${logo}")` : undefined,
    backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat" }} />
);

export default function CmpSwitchChamber({ chamberName, loginEmail }: { chamberName?: string | null; loginEmail?: string | null }) {
  const strings = useEnumStrings();
  const text = strings.switchChamberText;
  const loginText = strings.memberLoginText;
  const { data } = useGetMyMemberDashboardChambersQuery({ fetchPolicy: "cache-and-network",
    context: { preserveErrorForCaller: true } });
  const chambers: SwitchChamberRow[] = data?.getMyMemberDashboardChambers || [];
  const org = getOrganizationNoun(chamberName);
  const isChamber = org === "Chamber";
  const active = chambers.find(row => row.isActive) || chambers[0];
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loginFor, setLoginFor] = useState<SwitchChamberRow | null>(null);
  const [email, setEmail] = useState(loginEmail || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const options = useMemo(() => chambers.filter(row => row.name.toLowerCase().includes(query.trim().toLowerCase())), [chambers, query]);

  if (chambers.length < 2) return null;
  const sel = chambers.find(row => row.chamberId === selected) || active;

  const openWindow = () => { setSelected(active?.chamberId || null); setQuery(""); setDropdownOpen(false); setOpen(true); };
  const openDashboard = () => {
    if (!sel) return;
    if (sel.isActive) { setOpen(false); return; }
    setOpen(false); setPassword(""); setError(""); setEmail(loginEmail || ""); setLoginFor(sel);
  };
  const logIn = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!loginFor || busy) return;
    // Clear the field at once: the password never stays on screen while signing in.
    const typedPassword = password;
    setPassword("");
    setBusy(true); setError("");
    try {
      const user = await Auth.signIn(email.trim().toLowerCase(), typedPassword);
      if (user.challengeName) throw Object.assign(new Error("challenge"), { code: "NotAuthorizedException" });
      const chosen = await selectSignedInMemberChamber(user, loginFor.chamberId);
      if (chosen !== true) {
        setError(chosen === false ? fill(text.notLinked, { chamberName: loginFor.name }) : text.failed);
        setBusy(false);
        return;
      }
      window.location.href = RedirectUri.home;
    } catch (failure) {
      const code = (failure as { code?: string })?.code;
      setError(code === "NotAuthorizedException" || code === "UserNotFoundException"
        ? strings.loginErrorMessages.invalidCredentials : text.failed);
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" data-testid="switch-chamber-button" onClick={openWindow} style={{ display: "inline-flex", alignItems: "center", gap: 7,
        background: "#FFFFFF", border: `1.5px solid ${GOLD}`, color: GOLD_DARK, borderRadius: 8, padding: "6px 14px",
        fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h13l-3.5-3.5M20 17H7l3.5 3.5" /></svg>
        {fill(text.button, { org })}
      </button>

      {open ? (
        <div role="dialog" aria-modal="true" aria-labelledby="switch-chamber-title" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1450, padding: 24, fontFamily: "'DM Sans', sans-serif" }}>
          <div style={{ background: "#FFFFFF", borderRadius: 16, width: "min(760px, 94vw)", maxHeight: "92vh", overflowY: "auto",
            padding: "38px 40px 24px", boxSizing: "border-box", position: "relative", boxShadow: "0 8px 30px rgba(0,0,0,.15)" }}>
            <button type="button" aria-label={text.close} onClick={() => setOpen(false)} style={{ position: "absolute", top: 14, right: 14, width: 28, height: 28,
              borderRadius: "50%", background: "#ED1C24", color: "#FFFFFF", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>✕</button>
            <div id="switch-chamber-title" style={{ fontSize: 26, fontWeight: 700, color: BLACK, textAlign: "center", margin: "0 0 14px" }}>{fill(text.title, { org })}</div>
            <div style={{ fontSize: 15, color: BLACK, textAlign: "center", lineHeight: 1.6, maxWidth: 520, margin: "0 auto 30px" }}>
              {fill(text.body, { orgLower: org.toLowerCase() })}
            </div>
            {chambers.length <= 4 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 28, maxWidth: 610, margin: "0 auto" }}>
                {chambers.map(row => {
                  const picked = sel?.chamberId === row.chamberId;
                  return (
                    <button type="button" key={row.chamberId} data-testid="switch-chamber-card" aria-pressed={picked} onClick={() => setSelected(row.chamberId)} style={{
                      background: "#FFFFFF", border: picked ? `2px solid ${GOLD}` : "1px solid #ECECEE", borderRadius: 24,
                      boxShadow: picked ? "0 6px 18px rgba(225,167,49,.28)" : "0 2px 10px rgba(0,0,0,.06)", padding: "28px 20px 24px",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 20, cursor: "pointer", boxSizing: "border-box", fontFamily: "inherit" }}>
                      {logoBox(row.logo, row.name, 150, 110)}
                      <span style={{ fontSize: 17, fontWeight: 700, color: BLACK, textAlign: "center", lineHeight: 1.35 }}>{row.name}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div style={{ maxWidth: 460, margin: "0 auto", position: "relative", minHeight: dropdownOpen ? 360 : 0 }}>
                <button type="button" onClick={() => setDropdownOpen(!dropdownOpen)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 10, background: "#FFFFFF", border: "1px solid #D8D8D8", borderRadius: 8, padding: "12px 14px", fontFamily: "inherit", fontSize: 14,
                  color: sel ? BLACK : "#727272", cursor: "pointer", boxSizing: "border-box", textAlign: "left" }}>
                  {sel ? sel.name : fill(text.select, { orgLower: org.toLowerCase() })}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                {dropdownOpen ? (
                  <div style={{ position: "absolute", top: 54, left: 0, right: 0, background: "#FFFFFF", border: "1px solid #D8D8D8", borderRadius: 10,
                    boxShadow: "0 8px 30px rgba(0,0,0,.12)", zIndex: 2, overflow: "hidden" }}>
                    <div style={{ padding: "10px 12px", borderBottom: "1px solid #ECECEC" }}>
                      <input aria-label={isChamber ? text.searchChambers : text.searchBoards} placeholder={isChamber ? text.searchChambers : text.searchBoards}
                        value={query} onChange={event => setQuery(event.target.value)} style={{ width: "100%", boxSizing: "border-box", fontFamily: "inherit",
                          fontSize: 14, border: "1px solid #D8D8D8", borderRadius: 8, padding: "9px 12px", outline: "none" }} />
                    </div>
                    <div style={{ maxHeight: 220, overflowY: "auto" }}>
                      {options.map(row => (
                        <button type="button" key={row.chamberId} onClick={() => { setSelected(row.chamberId); setDropdownOpen(false); }} style={{
                          display: "block", width: "100%", textAlign: "left", border: "none", padding: "11px 14px", fontSize: 14, fontFamily: "inherit",
                          color: BLACK, fontWeight: sel?.chamberId === row.chamberId ? 600 : 400, background: sel?.chamberId === row.chamberId ? "#FDF6E7" : "transparent", cursor: "pointer" }}>{row.name}</button>
                      ))}
                      {!options.length ? <div style={{ padding: 14, fontSize: 13, color: "#727272" }}>{isChamber ? text.noMatchChambers : text.noMatchBoards}</div> : null}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
            {sel ? (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 30 }}>
                <button type="button" data-testid="switch-chamber-open" onClick={openDashboard} style={{ background: GOLD, color: "#FFFFFF", border: "none",
                  borderRadius: 12, padding: "14px 44px", fontFamily: "inherit", fontSize: 17, fontWeight: 700, cursor: "pointer" }}>{text.openDashboard}</button>
              </div>
            ) : null}
            <div style={{ borderTop: "1px solid #ECECEE", marginTop: 32, paddingTop: 16, textAlign: "center", fontSize: 14, color: BLACK }}>
              {text.signedInAs} <b>{loginEmail}</b>
            </div>
          </div>
        </div>
      ) : null}

      {loginFor ? (
        <div role="dialog" aria-modal="true" aria-label={fill(loginText.welcomeTitleLine2, { chamberName: loginFor.name })} style={{ position: "fixed", inset: 0, zIndex: 2000,
          background: "#FFFFFF", overflowY: "auto", display: "flex", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
          <button type="button" onClick={() => { setLoginFor(null); setOpen(true); }} style={{ position: "absolute", top: 24, left: 28, display: "inline-flex",
            alignItems: "center", gap: 8, background: "none", border: "none", color: GOLD_DARK, fontFamily: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
            {text.back}
          </button>
          <form onSubmit={logIn} style={{ width: "min(640px, 92vw)", padding: "44px 0 64px", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ marginBottom: 22 }}>{logoBox(loginFor.logo, loginFor.name, 240, 120)}</div>
            <div style={{ fontSize: 17, color: BLACK, textAlign: "center", marginBottom: 8 }}>{loginText.welcomeTitleLine1}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: BLACK, textAlign: "center", lineHeight: 1.3, marginBottom: 18 }}>
              {fill(loginText.welcomeTitleLine2, { chamberName: loginFor.name })}
            </div>
            <div style={{ fontSize: 15, color: BLACK, textAlign: "center", lineHeight: 1.6, maxWidth: 600, marginBottom: 48 }}>{loginText.description}</div>
            {error ? <div role="alert" style={{ width: "100%", marginBottom: 16, color: "#B91C1C", fontSize: 14 }}>{error}</div> : null}
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 20 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14, fontWeight: 700, color: BLACK }}>
                {loginText.emailLabel}
                <input id="switch-chamber-email" type="email" autoComplete="username" placeholder={loginText.emailPlaceholder} value={email}
                  onChange={event => setEmail(event.target.value)} style={{ boxSizing: "border-box", fontFamily: "inherit", fontSize: 15, fontWeight: 400,
                    border: "1px solid #D8D8D8", borderRadius: 8, padding: "14px 16px", outline: "none" }} />
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label htmlFor="switch-chamber-password" style={{ fontSize: 14, fontWeight: 700, color: BLACK }}>{loginText.passwordLabel}</label>
                <span style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <input id="switch-chamber-password" type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder={loginText.passwordPlaceholder}
                    value={password} onChange={event => setPassword(event.target.value)} style={{ width: "100%", boxSizing: "border-box", fontFamily: "inherit",
                      fontSize: 15, fontWeight: 400, border: "1px solid #D8D8D8", borderRadius: 8, padding: "14px 48px 14px 16px", outline: "none" }} />
                  <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 12,
                    background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#727272" strokeWidth="1.8" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>
                  </button>
                </span>
                <Link to={RedirectUri.forgotPassword} style={{ alignSelf: "flex-end", fontSize: 14, fontWeight: 400, color: GOLD_DARK, textDecoration: "underline", marginTop: 2 }}>
                  {loginText.forgotPasswordText}
                </Link>
              </div>
            </div>
            <button type="submit" disabled={busy || !email.trim() || (!password && !busy)} style={{ marginTop: 34, background: GOLD, color: "#FFFFFF", border: "none",
              borderRadius: 8, padding: "15px 56px", fontFamily: "inherit", fontSize: 16, fontWeight: 700, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}>
              {loginText.loginButton}
            </button>
            <div style={{ fontSize: 15, color: BLACK, textAlign: "center", lineHeight: 1.6, marginTop: 22, maxWidth: 420 }}>
              {loginText.noCredentialsText.split("{{chamberName}}")[0]}<b>{loginFor.name}</b>{loginText.noCredentialsText.split("{{chamberName}}")[1] || ""}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginTop: 56 }}>
              <div style={{ fontSize: 12, color: BLACK }}>{loginText.poweredByText}</div>
              <img src={OneChamberSystemLogo} alt="ONE Chamber System" style={{ width: 150, objectFit: "contain" }} />
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
}
